import {
  FETCH_USER,
  FETCH_USER_SUCCESS,
  FETCH_USER_FAILED,
  USER_SIGN_IN,
  USER_SIGN_IN_FAILED,
  USER_SIGN_OUT,
  CLEAR_LOGIN_ERROR,
  REQUEST_OTP,
  REQUEST_OTP_SUCCESS,
  REQUEST_OTP_FAILED,
  UPDATE_USER_WALLET_HISTORY,
  SEND_RESET_EMAIL,
  SEND_RESET_EMAIL_FAILED
} from "../store/types";

import store from '../store/store';
import { firebase } from '../config/configureFirebase';
import { onValue, update, set, off, get, ref } from "firebase/database";
import { onAuthStateChanged, signInWithCredential, signInWithPopup, signInWithRedirect, getRedirectResult, signOut as fbSignOut, sendPasswordResetEmail, confirmPasswordReset, verifyPasswordResetCode, signInWithEmailAndPassword, signInWithCustomToken, GoogleAuthProvider, setPersistence, browserLocalPersistence, linkWithCredential, PhoneAuthProvider, EmailAuthProvider, reauthenticateWithCredential, updatePassword } from "firebase/auth";
import { uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { isIOS, isPWA, safeRemove, setLocalWithTTL, getLocalWithTTL, logAuth } from '../other/authHelpers';

const recordSecuritySessionLogin = async (user) => {
  try {
    if (!user || typeof fetch === "undefined") return;
    const tokenResult = await user.getIdTokenResult();
    const authTime = tokenResult?.claims?.auth_time;
    const sessionKey = `sec_login_${user.uid}_${authTime}`;
    if (typeof sessionStorage !== "undefined" && sessionStorage.getItem(sessionKey)) {
      return;
    }
    const token = tokenResult.token;
    const projectId = firebase.config?.projectId || "drive2go-c806b";
    const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
    const baseUrl = (typeof process !== "undefined" && process.env?.REACT_APP_FUNCTIONS_URL)
      || `https://${region}-${projectId}.cloudfunctions.net`;

    await fetch(`${baseUrl}/recordLoginEvent`, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        "Authorization": `Bearer ${token}`
      }
    });
    if (typeof sessionStorage !== "undefined") {
      sessionStorage.setItem(sessionKey, "1");
    }
  } catch (_) { }
};

const getNativeAuthModule = () => {
  if (typeof document === 'undefined') {
    try {
      const rnf = typeof require === 'function' ? require('@react-native-firebase/auth') : null;
      return rnf?.default || rnf;
    } catch (_) { }
  }
  return null;
};

// Map to track active real-time session listeners to prevent duplicate bindings
const activeSessionListeners = new Map();

// Session epoch & active UID tracking to prevent stale async profile loads or cross-account listener bleed
let authSessionEpoch = 0;
let currentActiveAuthUid = null;

const isStaleAuthOperation = (expectedEpoch, expectedUid) => {
  if (expectedEpoch !== undefined && expectedEpoch !== authSessionEpoch) {
    return true;
  }
  if (expectedUid && currentActiveAuthUid && currentActiveAuthUid !== expectedUid) {
    return true;
  }
  return false;
};

const detachUserRealtimeListeners = (targetUid) => {
  if (!targetUid) return;
  if (activeSessionListeners.has(targetUid)) {
    try {
      const cleanup = activeSessionListeners.get(targetUid);
      activeSessionListeners.delete(targetUid);
      if (typeof cleanup === 'function') cleanup();
    } catch (_) {}
  }
  try {
    const { singleUserRef, walletHistoryRef, userNotificationsRef, tasksRef } = firebase;
    if (singleUserRef) off(singleUserRef(targetUid));
    if (walletHistoryRef) off(walletHistoryRef(targetUid));
    if (userNotificationsRef) off(userNotificationsRef(targetUid));
    if (tasksRef) off(tasksRef(targetUid));
  } catch (_) {}
};

/**
 * Vérifie si le compte d'un utilisateur a été marqué comme révoqué / supprimé
 * dans la table publique `revoked_users`. Cette vérification fonctionne même si le JWT a expiré.
 */
export const checkAccountRevokedStatus = async (uid) => {
  if (!uid) return false;
  let controller = null;
  let timeoutId = null;
  try {
    const { config } = firebase;
    const dbUrl = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
    if (typeof AbortController !== 'undefined') {
      controller = new AbortController();
      timeoutId = setTimeout(() => {
        try { controller.abort(); } catch (_) {}
      }, 3000);
    }
    const res = await fetch(`${dbUrl}/revoked_users/${uid}.json`, {
      signal: controller ? controller.signal : undefined
    });
    if (timeoutId) clearTimeout(timeoutId);
    if (res.ok) {
      const data = await res.json();
      if (data && (data.accountDeleted || data.isDeleted || data.revokedAt)) {
        return true;
      }
    }
  } catch (_) {
    if (timeoutId) clearTimeout(timeoutId);
  }
  return false;
};

/**
 * Attache un écouteur temps réel (RTDB websocket) pour détecter immédiatement
 * la suppression administrative du compte et forcer la déconnexion instantanée
 * sur l'application mobile et l'application web.
 */
export const attachUserSessionWatcher = (uid, dispatch) => {
  if (!uid || typeof dispatch !== 'function') return () => {};

  if (activeSessionListeners.has(uid)) {
    return activeSessionListeners.get(uid);
  }

  const { singleUserRef, db, database } = firebase;
  const currentDb = db || database;

  const cleanupFns = [];

  const handleTermination = (reason) => {
    console.warn(`[attachUserSessionWatcher] Immediate termination triggered for ${uid}: ${reason}. Signing off...`);
    if (activeSessionListeners.has(uid)) {
      try {
        const cleanup = activeSessionListeners.get(uid);
        activeSessionListeners.delete(uid);
        if (typeof cleanup === 'function') cleanup();
      } catch (_) {}
    }
    try {
      const AsyncStorage = typeof require === 'function' ? (require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage')) : null;
      if (AsyncStorage) {
        AsyncStorage.removeItem('driver2go_auth_session').catch(() => {});
      }
    } catch (_) {}
    try {
      if (typeof localStorage !== 'undefined') {
        localStorage.removeItem('driver2go_auth_session');
      }
    } catch (_) {}

    dispatch({
      type: USER_SIGN_OUT,
      payload: null
    });
    dispatch({
      type: FETCH_USER_FAILED,
      payload: { code: 'ACCOUNT_DELETED', message: 'ACCOUNT_DELETED' }
    });
    dispatch(signOff());
  };

  // 1. Écouteur RTDB sur users/${uid}
  if (singleUserRef) {
    try {
      const uRef = singleUserRef(uid);
      const unsubUser = onValue(uRef, (snapshot) => {
        if (!snapshot.exists()) {
          handleTermination("User node no longer exists in users/" + uid);
          return;
        }
        const val = snapshot.val();
        if (val && (val.accountDeleted === true || val.isDeleted === true || val.isActive === false)) {
          handleTermination("Account marked deleted/inactive in users/" + uid);
          return;
        }
      }, (err) => {
        if (err?.message && (err.message.includes("Permission denied") || err.message.includes("PERMISSION_DENIED"))) {
          handleTermination("RTDB Permission denied on users/" + uid);
        }
      });
      cleanupFns.push(() => {
        try {
          if (typeof unsubUser === 'function') unsubUser();
          else if (firebase.off && uRef) firebase.off(uRef);
        } catch (_) {}
      });
    } catch (e) {
      console.warn("[attachUserSessionWatcher] singleUserRef attach error:", e.message);
    }
  }

  // 2. Écouteur RTDB public sur revoked_users/${uid}
  if (currentDb && typeof ref === 'function') {
    try {
      const revokedRef = ref(currentDb, `revoked_users/${uid}`);
      const unsubRevoked = onValue(revokedRef, (snapshot) => {
        if (snapshot.exists() && snapshot.val()) {
          handleTermination("User present in revoked_users/" + uid);
        }
      }, () => {});
      cleanupFns.push(() => {
        try {
          if (typeof unsubRevoked === 'function') unsubRevoked();
          else if (firebase.off && revokedRef) firebase.off(revokedRef);
        } catch (_) {}
      });
    } catch (e) {
      console.warn("[attachUserSessionWatcher] revoked_users attach error:", e.message);
    }
  }

  const cleanupAll = () => {
    cleanupFns.forEach(fn => {
      try { fn(); } catch (_) {}
    });
    activeSessionListeners.delete(uid);
  };

  activeSessionListeners.set(uid, cleanupAll);
  return cleanupAll;
};

export const handleUserAuthenticated = async (user, dispatch, extraUserData = {}) => {
  if (!user || !user.uid) return;
  if (currentActiveAuthUid && currentActiveAuthUid !== user.uid) {
    detachUserRealtimeListeners(currentActiveAuthUid);
  }
  const sessionEpoch = ++authSessionEpoch;
  currentActiveAuthUid = user.uid;

  recordSecuritySessionLogin(user);

  const { config, singleUserRef, auth, authRef } = firebase;
  const currentWebAuth = auth || (typeof authRef === 'function' ? authRef() : null);

  // 1. Extraire les métadonnées de profil du provider
  let data = {
    uid: user.uid,
    mobile: '',
    email: '',
    firstName: '',
    lastName: '',
    verifyId: '',
    provider: 'google.com',
    authProvider: 'google.com',
    source: typeof document === 'undefined' ? 'mobile_app' : 'web_app',
    clientSource: typeof document === 'undefined' ? 'mobile_app' : 'web_app'
  };

  // Support direct des données retournées par GoogleSignin (givenName, familyName, photo, email)
  if (extraUserData) {
    if (extraUserData.givenName) data.firstName = extraUserData.givenName;
    if (extraUserData.familyName) data.lastName = extraUserData.familyName;
    if ((!data.firstName || !data.lastName) && extraUserData.name) {
      const parts = extraUserData.name.trim().split(" ");
      if (!data.firstName) data.firstName = parts[0] || '';
      if (!data.lastName) data.lastName = parts.slice(1).join(" ") || '';
    }
    if (extraUserData.email && !data.email) data.email = extraUserData.email;
    if (extraUserData.photo && !data.profile_image) data.profile_image = extraUserData.photo;
  }

  const providerData = user.providerData || [];
  if (providerData.length === 0 && user.email) {
    data.email = user.email;
  }
  if (providerData.length > 0 && user.phoneNumber) {
    data.mobile = user.phoneNumber;
  }
  if (user.phoneNumber && !data.mobile) {
    data.mobile = user.phoneNumber;
  }
  if (providerData.length > 0) {
    const provideData = providerData[0];
    if (provideData === 'phone') {
      data.mobile = provideData.phoneNumber;
      data.provider = 'phone';
      data.authProvider = 'phone';
    }
    if (provideData.providerId === "google.com" || provideData.providerId === 'apple.com') {
      if (provideData.providerId) {
        data.provider = provideData.providerId;
        data.authProvider = provideData.providerId;
      }
      if (provideData.email) data.email = provideData.email;
      if (provideData.phoneNumber) data.mobile = provideData.phoneNumber;
      if (provideData.displayName && (!data.firstName || !data.lastName)) {
        const parts = (provideData.displayName || '').trim().split(" ");
        if (parts.length > 1) {
          if (!data.firstName) data.firstName = parts[0];
          if (!data.lastName) data.lastName = parts.slice(1).join(" ");
        } else if (!data.firstName) {
          data.firstName = provideData.displayName;
        }
      }
      if (provideData.photoURL && !data.profile_image) {
        data['profile_image'] = provideData.photoURL;
      }
    }
  }
  if (!data.email && user.email) data.email = user.email;
  if ((!data.firstName || !data.lastName) && user.displayName) {
    const parts = (user.displayName || '').trim().split(" ");
    if (parts.length > 1) {
      if (!data.firstName) data.firstName = parts[0];
      if (!data.lastName) data.lastName = parts.slice(1).join(" ");
    } else if (!data.firstName) {
      data.firstName = user.displayName;
    }
  }
  if (!data.profile_image && user.photoURL) {
    data['profile_image'] = user.photoURL;
  }
  if (user.verifyId) {
    data.verifyId = user.verifyId;
  }
  data.displayName = `${data.firstName || ''} ${data.lastName || ''}`.trim();

  // 2. Synchroniser le profil et obtenir le customToken via check_auth_exists (Cloud Functions)
  let loadedProfile = null;
  try {
    const { config } = firebase;
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL) ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
    const activeProjectId = config?.projectId || firebase.config?.projectId || "drive2go-c806b";
    const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : `https://${region}-${activeProjectId}.cloudfunctions.net`);
    let url = `${host}/check_auth_exists`;
    const idToken = (user && typeof user.getIdToken === 'function') ? await user.getIdToken() : null;
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(data)
    });
    const json = await response.json();
    if (isStaleAuthOperation(sessionEpoch, user.uid)) {
      return;
    }
    if (json.uid) {
      loadedProfile = json;

      // Connecter le SDK Web JS avec le customToken pour que RTDB ait les permissions requises
      const webAuth = currentWebAuth || firebase.auth || (typeof firebase.authRef === 'function' ? firebase.authRef() : null);
      if (json.customToken && webAuth && (!webAuth.currentUser || webAuth.currentUser.uid !== user.uid)) {
        try {
          await signInWithCustomToken(webAuth, json.customToken);
        } catch (tokErr) {
          console.warn("[handleUserAuthenticated] signInWithCustomToken warning:", tokErr?.message);
        }
      }

      if (isStaleAuthOperation(sessionEpoch, user.uid)) {
        return;
      }

      // Fetch full profile from RTDB REST to populate walletBalance and private fields stripped by Cloud Function
      let finalProfile = json;
      if (idToken && json.uid) {
        try {
          const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
          const rtdbUrl = `${rtdbBase}/users/${json.uid}.json?auth=${idToken}`;
          const rtdbRes = await fetch(rtdbUrl);
          if (rtdbRes.ok) {
            const rtdbData = await rtdbRes.json();
            if (rtdbData && typeof rtdbData === 'object') {
              if (user.phoneNumber && (!rtdbData.mobile || rtdbData.mobile !== user.phoneNumber)) {
                rtdbData.mobile = user.phoneNumber;
              }
              finalProfile = { ...json, ...rtdbData, uid: json.uid };
            }
          }
        } catch (rtdbErr) {
          console.warn("[handleUserAuthenticated] RTDB direct fetch warning:", rtdbErr?.message);
        }
      }

      if (isStaleAuthOperation(sessionEpoch, user.uid)) {
        return;
      }

      if (finalProfile.accountDeleted === true || finalProfile.isDeleted === true || finalProfile.isActive === false) {
        console.warn("[handleUserAuthenticated] Account deleted or suspended on server. Disconnecting session immediately...");
        dispatch(signOff());
        return;
      }

      try {
        const AsyncStorage = typeof require === 'function' ? (require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage')) : null;
        if (AsyncStorage && !isStaleAuthOperation(sessionEpoch, user.uid)) {
          let existingSession = {};
          try {
            const rawPrev = await AsyncStorage.getItem('driver2go_auth_session');
            if (rawPrev) {
              const parsedPrev = JSON.parse(rawPrev) || {};
              if (parsedPrev.uid === json.uid) {
                existingSession = parsedPrev;
              }
            }
          } catch (_) {}
          const tokenToSave = idToken || (user && typeof user.getIdToken === 'function' ? await user.getIdToken() : null) || existingSession.idToken;
          const refreshToSave = user?.stsTokenManager?.refreshToken || user?.refreshToken || existingSession.refreshToken;
          if (!isStaleAuthOperation(sessionEpoch, user.uid)) {
            AsyncStorage.setItem('driver2go_auth_session', JSON.stringify({
              ...existingSession,
              uid: json.uid,
              profile: finalProfile,
              idToken: tokenToSave,
              ...(refreshToSave ? { refreshToken: refreshToSave } : {}),
              savedAt: tokenToSave ? Date.now() : (existingSession.savedAt || Date.now())
            })).catch(() => { });
          }
        }
      } catch (_) { }

      if (isStaleAuthOperation(sessionEpoch, user.uid)) {
        return;
      }

      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: finalProfile
      });

      // Attacher l'observateur temps réel pour déconnexion immédiate si supprimé
      attachUserSessionWatcher(user.uid, dispatch);
    }
  } catch (syncErr) {
    console.warn("[handleUserAuthenticated] check_auth_exists sync warning:", syncErr?.message);
  }

  if (isStaleAuthOperation(sessionEpoch, user.uid)) {
    return;
  }

  // 3. Attacher l'écouteur temps réel RTDB (onValue)
  if (!singleUserRef) {
    const retryRef = setInterval(() => {
      if (isStaleAuthOperation(sessionEpoch, user.uid)) {
        clearInterval(retryRef);
        return;
      }
      if (firebase.singleUserRef) {
        clearInterval(retryRef);
        handleUserAuthenticated(user, dispatch);
      }
    }, 200);
    setTimeout(() => clearInterval(retryRef), 6000);
    return;
  }

  // Attachement de sécurité du session watcher
  attachUserSessionWatcher(user.uid, dispatch);

  try {
    off(singleUserRef(user.uid));
  } catch (_) {}

  onValue(singleUserRef(user.uid), async (snapshot) => {
    if (isStaleAuthOperation(sessionEpoch, user.uid)) {
      try { off(singleUserRef(user.uid)); } catch (_) {}
      return;
    }
    if (snapshot.val()) {
      let profile = snapshot.val();
      profile.uid = user.uid;
      if (user.phoneNumber && (!profile.mobile || profile.mobile !== user.phoneNumber)) {
        profile.mobile = user.phoneNumber;
      }

      // [CRITICAL] Si le compte a été supprimé par l'admin ou sur le serveur : déconnecter immédiatement !
      if (profile.accountDeleted === true || profile.isDeleted === true || profile.isActive === false) {
        console.warn("[handleUserAuthenticated] Account deleted/inactive on server. Disconnecting session immediately...");
        try {
          const { off } = firebase;
          if (off && singleUserRef) off(singleUserRef(user.uid));
        } catch (_) { }
        try {
          const AsyncStorage = typeof require === 'function' ? (require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage')) : null;
          if (AsyncStorage) {
            await AsyncStorage.removeItem('driver2go_auth_session').catch(() => { });
          }
        } catch (_) { }
        try {
          if (typeof localStorage !== 'undefined') {
            localStorage.removeItem('driver2go_auth_session');
          }
        } catch (_) { }
        dispatch({
          type: USER_SIGN_OUT,
          payload: null
        });
        dispatch({
          type: FETCH_USER_FAILED,
          payload: { code: 'ACCOUNT_DELETED', message: 'ACCOUNT_DELETED' }
        });
        dispatch(signOff());
        return;
      }

      try {
        const AsyncStorage = typeof require === 'function' ? (require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage')) : null;
        if (AsyncStorage && !isStaleAuthOperation(sessionEpoch, user.uid)) {
          AsyncStorage.getItem('driver2go_auth_session').then(rawPrev => {
            if (isStaleAuthOperation(sessionEpoch, user.uid)) return;
            let existingSession = {};
            if (rawPrev) {
              try {
                const parsedPrev = JSON.parse(rawPrev) || {};
                if (parsedPrev.uid === user.uid) {
                  existingSession = parsedPrev;
                }
              } catch (_) {}
            }
            return AsyncStorage.setItem('driver2go_auth_session', JSON.stringify({
              ...existingSession,
              uid: user.uid,
              profile
            }));
          }).catch(() => { });
        }
      } catch (_) { }

      if (!isStaleAuthOperation(sessionEpoch, user.uid)) {
        dispatch({
          type: FETCH_USER_SUCCESS,
          payload: profile
        });
      }
    }
  }, (error) => {
    if (isStaleAuthOperation(sessionEpoch, user.uid)) {
      return;
    }
    console.warn("[handleUserAuthenticated] RTDB onValue warning:", error?.message);
    // Si la permission est refusée (compte révoqué/supprimé), déconnecter proprement
    if (error?.message && (error.message.includes("Permission denied") || error.message.includes("PERMISSION_DENIED"))) {
      console.warn("[handleUserAuthenticated] RTDB permission denied. Account may be deleted. Signing off...");
      dispatch({
        type: USER_SIGN_OUT,
        payload: null
      });
      dispatch({
        type: FETCH_USER_FAILED,
        payload: { code: 'ACCOUNT_DELETED', message: 'ACCOUNT_DELETED' }
      });
      dispatch(signOff());
      return;
    }
    // Si aucun profil n'a été chargé par check_auth_exists, alors seulement remonter l'erreur
    if (!loadedProfile) {
      dispatch({
        type: FETCH_USER_FAILED,
        payload: { code: "PERMISSION_DENIED", message: error.message }
      });
    }
  });
};

export const fetchUser = () => (dispatch) => {
  const {
    auth,
    authRef,
    singleUserRef
  } = firebase;

  const currentAuth = auth || (typeof authRef === 'function' ? authRef() : null);

  dispatch({
    type: FETCH_USER,
    payload: null
  });

  // 1. Écouteur natif (@react-native-firebase/auth) pour environnement mobile
  const nativeAuthModule = getNativeAuthModule();
  if (nativeAuthModule && typeof nativeAuthModule === 'function') {
    try {
      const nativeAuth = nativeAuthModule();
      if (nativeAuth && typeof nativeAuth.onAuthStateChanged === 'function') {
        nativeAuth.onAuthStateChanged((user) => {
          if (user) {
            handleUserAuthenticated(user, dispatch);
          } else if (!currentAuth) {
            const langData = store.getState().languagedata;
            const defaultLang = langData && langData.defaultLanguage ? langData.defaultLanguage : { auth_error: 'Auth Error', not_logged_in: 'Not logged in' };
            dispatch({
              type: FETCH_USER_FAILED,
              payload: { code: defaultLang.auth_error, message: defaultLang.not_logged_in }
            });
          }
        });
        if (nativeAuth.currentUser) {
          handleUserAuthenticated(nativeAuth.currentUser, dispatch);
        }
      }
    } catch (e) {
      console.warn("[fetchUser] native onAuthStateChanged warning:", e?.message);
    }
  }

  // 2. Écouteur Web JS SDK (Firebase modular)
  if (currentAuth && typeof onAuthStateChanged === 'function') {
    onAuthStateChanged(currentAuth, (user) => {
      if (user) {
        handleUserAuthenticated(user, dispatch);
      } else if (!nativeAuthModule) {
        const langData = store.getState().languagedata;
        const defaultLang = langData && langData.defaultLanguage ? langData.defaultLanguage : { auth_error: 'Auth Error', not_logged_in: 'Not logged in' };
        dispatch({
          type: FETCH_USER_FAILED,
          payload: { code: defaultLang.auth_error, message: defaultLang.not_logged_in }
        });
      }
    });
    // Sur web, attendre que onAuthStateChanged résolve la session
    if (typeof document !== 'undefined') {
      return;
    }
  }

  // 3. Fallback de cache rapide (AsyncStorage)
  const fetchEpoch = authSessionEpoch;
  let handledByAsyncStorage = false;
  try {
    let AsyncStorage = null;
    try {
      AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
    } catch (_) { }
    if (AsyncStorage) {
      handledByAsyncStorage = true;
      AsyncStorage.getItem('driver2go_auth_session').then(rawSession => {
        if (isStaleAuthOperation(fetchEpoch)) {
          return;
        }
        if (rawSession) {
          try {
            const session = JSON.parse(rawSession);
            if (session && session.profile && session.uid) {
              if (currentActiveAuthUid && currentActiveAuthUid !== session.uid) {
                return;
              }
              if (session.profile.accountDeleted === true || session.profile.isDeleted === true || session.profile.isActive === false) {
                console.warn("[fetchUser] Stored session is marked deleted/inactive. Purging and failing auth...");
                AsyncStorage.removeItem('driver2go_auth_session').catch(() => {});
                dispatch({
                  type: FETCH_USER_FAILED,
                  payload: { code: 'ACCOUNT_DELETED', message: 'ACCOUNT_DELETED' }
                });
                return;
              }
              currentActiveAuthUid = session.uid;
              dispatch({
                type: FETCH_USER_SUCCESS,
                payload: session.profile
              });
              attachUserSessionWatcher(session.uid, dispatch);
              setTimeout(() => {
                try {
                  if (!isStaleAuthOperation(undefined, session.uid)) {
                    dispatch(refreshUserProfile());
                  }
                } catch (_) { }
              }, 300);
              return;
            }
          } catch (_) { }
        }
        if (!nativeAuthModule?.currentUser && !currentAuth?.currentUser && !currentActiveAuthUid) {
          dispatch({
            type: FETCH_USER_FAILED,
            payload: { code: 'NOT_LOGGED_IN', message: 'Not logged in' }
          });
        }
      }).catch(() => {
        if (!isStaleAuthOperation(fetchEpoch) && !nativeAuthModule?.currentUser && !currentAuth?.currentUser && !currentActiveAuthUid) {
          dispatch({
            type: FETCH_USER_FAILED,
            payload: { code: 'NOT_LOGGED_IN', message: 'Not logged in' }
          });
        }
      });
    }
  } catch (_) { }

  if (!handledByAsyncStorage && !nativeAuthModule?.currentUser && !currentAuth?.currentUser && !currentActiveAuthUid) {
    dispatch({
      type: FETCH_USER_FAILED,
      payload: { code: 'NOT_LOGGED_IN', message: 'Not logged in' }
    });
  }
};


export const checkUserExists = async (data) => {
  const {
    config
  } = firebase;

  // [FIX] Host Logic
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
  let host = origin || functionsUrl || `https://${config.projectId}.web.app`;
  let url = `${host}/check_user_exists`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      email: data.email,
      mobile: data.mobile,
      uid: data.uid
    })
  })
  const json = await response.json();
  return json;
};

export const mainSignUp = async (regData) => {
  const {
    config
  } = firebase;
  // [FIX] Host Logic
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
  let host = origin || functionsUrl || `https://${config.projectId}.web.app`;
  let url = `${host}/user_signup`;
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ regData: regData })
  })
  const res = await response.json();
  return res;
};

export const updateProfileWithEmail = (profileData) => async (dispatch) => {
  const {
    config,
    auth
  } = firebase;
  try {
    // [FIX] Host Logic
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/update_user_email`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(profileData)
    })
    const result = await response.json();
    if (result.error) {
      return { success: false, error: result.error }
    }
  } catch (error) {
    return { success: false, error: error }
  }
}

export const changePassword = (currentPassword, newPassword) => async (dispatch) => {
  const { auth } = firebase;
  const user = auth?.currentUser;
  if (!user) return { success: false, error: "User not logged in" };

  try {
    const credential = EmailAuthProvider.credential(user.email, currentPassword);
    await reauthenticateWithCredential(user, credential);
    await updatePassword(user, newPassword);
    return { success: true };
  } catch (error) {
    return { success: false, error: error.message };
  }
};


export const requestPhoneOtpDevice = (verificationId) => async (dispatch) => {
  dispatch({
    type: REQUEST_OTP_SUCCESS,
    payload: verificationId
  });
}

export const mobileSignIn = (verificationId, code) => async (dispatch) => {
  const { auth, authRef, singleUserRef } = firebase;
  const currentAuth = auth || (typeof authRef === 'function' ? authRef() : null);

  dispatch({ type: USER_SIGN_IN, payload: null });

  // 1. Priorité au module natif @react-native-firebase/auth sur mobile (Android / iOS)
  const nativeAuthModule = getNativeAuthModule();
  if (nativeAuthModule && typeof nativeAuthModule === 'function') {
    try {
      const nativeAuth = nativeAuthModule();
      if (nativeAuthModule.PhoneAuthProvider && typeof nativeAuthModule.PhoneAuthProvider.credential === 'function') {
        const credential = nativeAuthModule.PhoneAuthProvider.credential(verificationId, code);
        const result = await nativeAuth.signInWithCredential(credential);
        if (result?.user) {
          if (singleUserRef) {
            const updateData = {
              mobile: result.user.phoneNumber,
              phoneVerified: true,
              phoneVerifiedAt: new Date().toISOString()
            };
            try {
              update(singleUserRef(result.user.uid), updateData);
            } catch (_) { }
          }
          handleUserAuthenticated(result.user, dispatch);
          return { success: true, user: result.user };
        }
      }
    } catch (nativeErr) {
      console.error("[OTP_LOG] mobileSignIn native error:", nativeErr?.code, nativeErr?.message);
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: {
          code: nativeErr?.code || 'auth/native-error',
          message: nativeErr?.message || 'Erreur lors de la vérification de l\'OTP'
        }
      });
      return { success: false, error: nativeErr?.message };
    }
  }

  // 2. Fallback Web JS SDK (pour environnement Web navigateur)
  if (!currentAuth) {
    console.error("[OTP_LOG] mobileSignIn: Firebase auth instance is not initialized.");
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: { code: 'auth/not-initialized', message: 'Firebase auth instance is not initialized' }
    });
    return { success: false, error: 'Firebase auth instance is not initialized' };
  }

  try {
    const credential = PhoneAuthProvider.credential(verificationId, code);

    const result = await signInWithCredential(currentAuth, credential);
    if (singleUserRef && result?.user) {
      const updateData = {
        mobile: result.user.phoneNumber,
        phoneVerified: true,
        phoneVerifiedAt: new Date().toISOString()
      };
      try {
        update(singleUserRef(result.user.uid), updateData);
      } catch (_) { }
    }
    handleUserAuthenticated(result.user, dispatch);
    return { success: true, user: result.user };
  } catch (err) {
    console.error("[OTP_LOG] mobileSignIn credential error:", err?.message);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: err
    });
    return { success: false, error: err?.message };
  }
};

// [AUTH_GOOGLE_V1] Link/Sign-in Mobile OTP with stability guards
export const linkMobileOtp = (verificationId, code, phoneE164) => async (dispatch) => {
  const { auth, singleUserRef } = firebase;
  const user = auth?.currentUser;

  logAuth("linkMobileOtp start", { mode: user ? "LINK" : "SIGN_IN", phone: phoneE164 });
  dispatch({ type: USER_SIGN_IN, payload: null });

  try {
    const credential = PhoneAuthProvider.credential(verificationId, code);
    let userCredential;

    if (user) {
      userCredential = await linkWithCredential(user, credential);
      logAuth("link_phone success (LINK)", { uid: user.uid });
    } else {
      userCredential = await signInWithCredential(auth, credential);
      logAuth("link_phone success (SIGN_IN)", { uid: userCredential.user.uid });
    }

    const uid = userCredential.user.uid;
    const updateData = {
      mobile: userCredential.user.phoneNumber || phoneE164,
      phoneVerified: true,
      phoneVerifiedAt: new Date().toISOString()
    };

    await update(singleUserRef(uid), updateData);
    logAuth("profile_update success", { uid });
    return { success: true };

  } catch (error) {
    logAuth("link_phone failed", { code: error.code, msg: error.message });
    let errorMessage = error.message;

    if (error.code === 'auth/credential-already-in-use') {
      errorMessage = "Ce numéro est déjà lié à un autre compte.";
    } else if (error.code === 'auth/invalid-verification-code') {
      errorMessage = "Code de vérification invalide.";
    } else if (error.code === 'auth/code-expired') {
      errorMessage = "Code expiré. Veuillez demander un nouveau code.";
    }

    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: { ...error, message: errorMessage }
    });
    return { success: false, error: error.code, message: errorMessage };
  }
};

export const saveAddresses = async (uid, location, name) => {
  const { singleUserRef } = firebase;
  onValue(child(singleUserRef(uid), "savedAddresses"), (savedAdd) => {
    if (savedAdd.val()) {
      let addresses = savedAdd.val();
      let didNotMatch = true;
      for (let key in addresses) {
        let entry = addresses[key];
        if (entry.name == name) {
          didNotMatch = false;
          update(child(singleUserRef(uid), "savedAddresses/" + key), {
            description: location.add,
            lat: location.lat,
            lng: location.lng,
            count: 1,
            name: name
          });
          break;
        }
      }
      if (didNotMatch) {
        push(child(singleUserRef(uid), "savedAddresses"), {
          description: location.add,
          lat: location.lat,
          lng: location.lng,
          count: 1,
          name: name
        });
      }
    } else {
      push(child(singleUserRef(uid), "savedAddresses"), {
        description: location.add,
        lat: location.lat,
        lng: location.lng,
        count: 1,
        name: name
      });
    }
  }, { onlyOnce: true });
};

// [AUTH_GOOGLE_V1] Smart flow: Popup preferred, Redirect fallback for PWA/iOS
export const googleLogin = (idToken, accessToken, extraUserData) => async (dispatch) => {
  const { auth, authRef, googleCredential } = firebase;
  const currentWebAuth = auth || (typeof authRef === 'function' ? authRef() : null);

  dispatch({ type: USER_SIGN_IN, payload: null });

  if (idToken || (accessToken && typeof document === 'undefined')) {
    // Mobile Flow (Firebase Native / Credential)
    logAuth("mobile_credential_attempt");

    const nativeAuthModule = getNativeAuthModule();

    // 1. Tenter l'authentification native (@react-native-firebase/auth) si disponible
    if (nativeAuthModule && typeof nativeAuthModule === 'function') {
      try {
        const nativeAuth = nativeAuthModule();
        const nativeCred = nativeAuthModule.GoogleAuthProvider.credential(idToken, accessToken);
        const result = await nativeAuth.signInWithCredential(nativeCred);
        logAuth("mobile_credential_native_success", { uid: result?.user?.uid });

        // Synchroniser également avec l'instance Web JS SDK si présente
        if (currentWebAuth && typeof GoogleAuthProvider?.credential === 'function') {
          try {
            const webCred = GoogleAuthProvider.credential(idToken, accessToken);
            await signInWithCredential(currentWebAuth, webCred);
          } catch (syncErr) {
            console.warn("[googleLogin] web auth sync warning:", syncErr?.message);
          }
        }

        if (result?.user) {
          await handleUserAuthenticated(result.user, dispatch, extraUserData);
        }

        return { success: true, user: result?.user };
      } catch (nativeErr) {
        console.warn("[googleLogin] native auth attempt failed:", nativeErr?.message);
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: {
            code: nativeErr?.code || 'auth/google-error',
            message: nativeErr?.message || 'Erreur lors de la connexion Google'
          }
        });
        return { success: false, error: nativeErr?.message };
      }
    }

    // 2. Fallback Web JS SDK (Firebase Web modular)
    if (!currentWebAuth) {
      const err = new Error("Firebase Auth non initialisé");
      logAuth("mobile_credential_failed", "auth/not-initialized");
      dispatch({ type: USER_SIGN_IN_FAILED, payload: err });
      return { success: false, error: err.message };
    }

    const credential = typeof googleCredential === 'function'
      ? googleCredential(idToken, accessToken)
      : GoogleAuthProvider.credential(idToken, accessToken);

    try {
      const userCredential = await signInWithCredential(currentWebAuth, credential);
      logAuth("mobile_credential_success", { uid: userCredential.user.uid });
      if (userCredential?.user) {
        await handleUserAuthenticated(userCredential.user, dispatch, extraUserData);
      }
      return { success: true, user: userCredential.user };
    } catch (error) {
      logAuth("mobile_credential_failed", error.code);
      dispatch({ type: USER_SIGN_IN_FAILED, payload: error });
      return { success: false, error: error.message };
    }
  } else {
    // Web Flow: Smart Auth
    if (!currentWebAuth) {
      const err = new Error("Firebase Auth non initialisé");
      logAuth("web_auth_failed", "auth/not-initialized");
      dispatch({ type: USER_SIGN_IN_FAILED, payload: err });
      return { success: false, error: err.message };
    }

    const provider = new GoogleAuthProvider();
    provider.setCustomParameters({ prompt: 'select_account' });

    const pwa = isPWA();
    const ios = isIOS();
    logAuth("attempt", { isPWA: pwa, isIOS: ios, userAgent: navigator.userAgent });

    // Force Redirect in PWA/iOS standalone to avoid popup context loss
    if (pwa || ios) {
      logAuth("redirect_attempt (Force PWA/iOS)");
      setLocalWithTTL("googleRedirectState", { flow: "google_redirect", ts: Date.now() }, 10 * 60 * 1000);
      return signInWithRedirect(currentWebAuth, provider);
    }

    try {
      logAuth("popup_attempt");
      const result = await signInWithPopup(currentWebAuth, provider);
      logAuth("popup_success", { uid: result.user.uid });
      if (result?.user) {
        handleUserAuthenticated(result.user, dispatch);
      }
      return { success: true, user: result.user };
    } catch (error) {
      // Smart fallback: only use redirect when it's safe (same context)
      if (error.code === 'auth/popup-blocked' || error.code === 'auth/cancelled-popup-request' || error.code === 'auth/operation-not-supported-in-this-environment') {
        const isMobileUA = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

        // PWA/iOS: redirect is safe (we started AND will return in same context)
        if (pwa || ios) {
          logAuth("popup_blocked_in_pwa -> redirect_safe", error.code);
          setLocalWithTTL("googleRedirectState", { flow: "google_redirect", ts: Date.now() }, 10 * 60 * 1000);
          return signInWithRedirect(currentWebAuth, provider);
        }

        // Mobile BROWSER with PWA installed: redirect is DANGEROUS
        // Android will intercept /__/auth/handler URL and open it in PWA context
        // → sessionStorage mismatch → missing-initial-state → blank screen
        if (isMobileUA) {
          logAuth("popup_blocked_on_mobile_browser -> NO_REDIRECT", error.code);
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: {
              code: "AUTH_NEED_OPEN_APP",
              message: "Ouvrez Driver2Go depuis le raccourci (PWA) puis connectez-vous avec Google."
            }
          });
          return { success: false, error: "AUTH_NEED_OPEN_APP" };
        }

        // Desktop: redirect fallback OK (no PWA interception risk)
        logAuth("popup_blocked_desktop -> redirect_fallback", error.code);
        setLocalWithTTL("googleRedirectState", { flow: "google_redirect", ts: Date.now() }, 10 * 60 * 1000);
        return signInWithRedirect(currentWebAuth, provider);
      }

      logAuth("popup_failed", { code: error.code, msg: error.message });
      dispatch({ type: USER_SIGN_IN_FAILED, payload: error });
      return { success: false, error: error.message };
    }
  }
};

// [AUTH_GOOGLE] Helper: wait for Firebase to restore the session from IndexedDB
// This is critical for browser→PWA handoff where sessionStorage is partitioned
// but IndexedDB (and thus the auth session) IS shared across contexts.
const waitForAuthRestore = (auth, timeoutMs = 5000) => {
  return new Promise((resolve) => {
    if (!auth) return resolve(null);
    // If already available, resolve immediately
    if (auth.currentUser) {
      //console.log("[AUTH_GOOGLE] waitForAuthRestore: already available.");
      return resolve(auth.currentUser);
    }
    let settled = false;
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      if (!settled) {
        settled = true;
        unsubscribe();
        //console.log("[AUTH_GOOGLE] waitForAuthRestore: onAuthStateChanged fired. user:", user?.uid || "null");
        resolve(user);
      }
    });
    setTimeout(() => {
      if (!settled) {
        settled = true;
        unsubscribe();
        console.warn("[AUTH_GOOGLE] waitForAuthRestore: timed out after", timeoutMs, "ms");
        resolve(null);
      }
    }, timeoutMs);
  });
};

// [AUTH_GOOGLE] Enhanced bootstrap redirect handler with recovery
export const checkAuthRedirect = () => async (dispatch) => {
  const { auth, singleUserRef } = firebase;
  //console.log("[AUTH_GOOGLE] checkAuthRedirect:start");

  // Helper: fetch existing profile from RTDB and dispatch
  const fetchExistingProfile = async (uid) => {
    if (!firebase.database) return null;
    try {
      const snapshot = await get(singleUserRef(uid));
      if (snapshot.exists()) {
        const profile = snapshot.val();
        profile.uid = uid;
        if (profile.accountDeleted === true || profile.isDeleted === true || profile.isActive === false) {
          console.warn("[AUTH_GOOGLE] Account marked deleted. Signing off...");
          dispatch(signOff());
          return null;
        }
        dispatch({ type: FETCH_USER_SUCCESS, payload: profile });
        attachUserSessionWatcher(uid, dispatch);
        return profile;
      }
    } catch (e) {
      console.error("[AUTH_GOOGLE] fetchExistingProfile crash:", e);
    }
    return null;
  };

  // Read redirect marker (localStorage persists across browser↔PWA contexts)
  const redirectState = getLocalWithTTL("googleRedirectState");
  const hadRedirectState = !!redirectState;

  // A) RESTORED SESSION: If SDK already caught the user via IndexedDB
  if (auth?.currentUser) {
    const uid = auth.currentUser.uid;
    //console.log("[AUTH_GOOGLE] path=A. Session already restored. uid:", uid, "hadRedirectState:", hadRedirectState);
    const profile = await fetchExistingProfile(uid);
    if (profile) {
      //console.log("[AUTH_GOOGLE] path=A. Profile found.");
      // If we came from a redirect flow (browser→PWA), mark as redirect
      // so App.js triggers window.location.replace navigation
      if (hadRedirectState) {
        safeRemove("googleRedirectState");
        return { success: true, user: auth.currentUser, isRedirect: true, mode: "restored_post_error" };
      }
      return { success: true, user: auth.currentUser, isRedirect: false, mode: "restored" };
    } else {
      console.warn("[AUTH_GOOGLE] path=A. User exists but NO profile in RTDB.");
      return { success: false, reason: "profile_missing", user: auth.currentUser };
    }
  }

  // B) NO REDIRECT MARKER → skip getRedirectResult entirely
  // This avoids "missing initial state" from cross-context redirects (browser→PWA)
  if (!hadRedirectState) {
    //console.log("[AUTH_GOOGLE] path=B1. No redirect marker -> skip getRedirectResult. Waiting for onAuthStateChanged...");
    const user = await waitForAuthRestore(auth, 5000);
    if (user) {
      //console.log("[AUTH_GOOGLE] path=B1. User found via onAuthStateChanged. uid:", user.uid);
      const profile = await fetchExistingProfile(user.uid);
      if (profile) {
        //console.log("[AUTH_GOOGLE] path=B1. Profile found.");
        return { success: true, user: user, isRedirect: false, mode: "restored" };
      } else {
        console.warn("[AUTH_GOOGLE] path=B1. User found but NO profile.");
        return { success: false, reason: "profile_missing", user: user };
      }
    }
    //console.log("[AUTH_GOOGLE] path=B1. No user found (normal unauthenticated startup).");
    return { success: true, reason: "initial_idle" };
  }

  // B2) REDIRECT MARKER EXISTS → attempt getRedirectResult (redirect was started from this context)
  try {
    //console.log("[AUTH_GOOGLE] path=B2. Redirect marker found -> calling getRedirectResult...");
    const result = await getRedirectResult(auth);

    if (result && result.user) {
      const uid = result.user.uid;
      //console.log("[AUTH_GOOGLE] path=B2. redirectResult found user:", uid);
      safeRemove("googleRedirectState");
      const profile = await fetchExistingProfile(uid);
      if (profile) {
        //console.log("[AUTH_GOOGLE] path=B2. Profile found.");
        return { success: true, user: result.user, isRedirect: true, mode: "redirectResult" };
      } else {
        console.warn("[AUTH_GOOGLE] path=B2. Auth success but PROFILE MISSING.");
        return { success: false, reason: "profile_missing", user: result.user };
      }
    }

    //console.log("[AUTH_GOOGLE] path=B2. getRedirectResult returned null.");
    safeRemove("googleRedirectState");
    return { success: false, reason: "no_redirect_result" };

  } catch (error) {
    const errorMsg = error.message || "";
    const errorCode = error.code || "";
    console.error("[AUTH_GOOGLE] path=C. redirectResult failed:", errorCode, errorMsg);

    // C) MISSING INITIAL STATE fallback: wait for onAuthStateChanged
    if (errorCode === 'auth/missing-initial-state' || errorMsg.includes("missing initial state")) {
      //console.log("[AUTH_GOOGLE] path=C. missing-initial-state -> waitForAuthRestore (5s)...");
      const user = await waitForAuthRestore(auth, 5000);
      if (user) {
        //console.log("[AUTH_GOOGLE] path=C. User restored after wait. uid:", user.uid);
        safeRemove("googleRedirectState");
        const profile = await fetchExistingProfile(user.uid);
        if (profile) {
          //console.log("[AUTH_GOOGLE] path=C. Profile found. Recovery SUCCESS.");
          return { success: true, user: user, isRedirect: true, mode: "restored_post_error" };
        } else {
          console.warn("[AUTH_GOOGLE] path=C. User restored but NO profile.");
          return { success: false, reason: "profile_missing", user: user };
        }
      } else {
        console.warn("[AUTH_GOOGLE] path=C. No user found after 5s wait.");
      }
    }

    safeRemove("googleRedirectState");
    dispatch({ type: USER_SIGN_IN_FAILED, payload: error });
    return { success: false, error: errorMsg, code: errorCode };
  }
};

export const appleSignIn = (credentialData) => (dispatch) => {
  const {
    auth,
    authRef,
    appleProvider
  } = firebase;

  const currentAuth = auth || (typeof authRef === 'function' ? authRef() : null);

  dispatch({
    type: USER_SIGN_IN,
    payload: null
  });

  const nativeAuthModule = getNativeAuthModule();
  if (nativeAuthModule && typeof nativeAuthModule === 'function' && credentialData?.idToken) {
    try {
      const nativeAuth = nativeAuthModule();
      if (nativeAuthModule.AppleAuthProvider && typeof nativeAuthModule.AppleAuthProvider.credential === 'function') {
        const credential = nativeAuthModule.AppleAuthProvider.credential(credentialData.idToken, credentialData.rawNonce);
        nativeAuth.signInWithCredential(credential)
          .then(async (result) => {
            if (result?.user) {
              await handleUserAuthenticated(result.user, dispatch);
            }
          })
          .catch((error) => {
            dispatch({
              type: USER_SIGN_IN_FAILED,
              payload: error
            });
          });
        return;
      }
    } catch (_) { }
  }

  if (!currentAuth) {
    const err = new Error("Firebase Auth non initialisé");
    dispatch({ type: USER_SIGN_IN_FAILED, payload: err });
    return;
  }

  if (credentialData) {
    const credential = appleProvider.credential(credentialData);
    signInWithCredential(currentAuth, credential)
      .then(async (result) => {
        if (result?.user) {
          await handleUserAuthenticated(result.user, dispatch);
        }
      })
      .catch((error) => {
        dispatch({
          type: USER_SIGN_IN_FAILED,
          payload: error
        });
      });
  } else {
    signInWithPopup(currentAuth, appleProvider).then(function (result) {
      signInWithCredential(currentAuth, result.credential)
        .then((resultCred) => {
          if (resultCred?.user) {
            handleUserAuthenticated(resultCred.user, dispatch);
          }
        })
        .catch(error => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        }
        );
    }).catch(function (error) {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: error
      });
    });
  }
};

export const signOff = () => (dispatch, getState) => {
  const {
    auth,
    singleUserRef,
    walletHistoryRef,
    userNotificationsRef,
    tasksRef
  } = firebase;

  const state = typeof getState === 'function' ? getState() : null;
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid || currentActiveAuthUid;

  // Invalider immédiatement toute opération asynchrone de connexion/profil en cours
  authSessionEpoch += 1;
  currentActiveAuthUid = null;

  // 1. Nettoyer tous les observateurs de session actifs
  if (uid && activeSessionListeners.has(uid)) {
    try {
      const cleanup = activeSessionListeners.get(uid);
      activeSessionListeners.delete(uid);
      if (typeof cleanup === 'function') cleanup();
    } catch (_) {}
  }
  for (const [listenerUid, cleanup] of Array.from(activeSessionListeners.entries())) {
    try {
      activeSessionListeners.delete(listenerUid);
      if (typeof cleanup === 'function') cleanup();
    } catch (_) {}
  }

  // 2. Détacher les écouteurs RTDB et mettre hors service sans jamais réinitialiser activeMode
  const currentProfile = state?.auth?.profile || store.getState()?.auth?.profile;
  if (uid && singleUserRef) {
    try {
      off(singleUserRef(uid));
      if (walletHistoryRef) off(walletHistoryRef(uid));
      if (userNotificationsRef) off(userNotificationsRef(uid));
      if (tasksRef) off(tasksRef(uid));

      if (currentProfile && currentProfile.usertype === 'driver' && !currentProfile.accountDeleted && !currentProfile.isDeleted && currentProfile.isActive !== false) {
        update(singleUserRef(uid), { driverActiveStatus: false }).catch(() => { });
      } else {
        onValue(singleUserRef(uid), snapshot => {
          if (snapshot.val()) {
            const profile = snapshot.val();
            if (profile && profile.usertype === 'driver' && !profile.accountDeleted && !profile.isDeleted && profile.isActive !== false) {
              update(singleUserRef(uid), { driverActiveStatus: false }).catch(() => { });
            }
          }
        }, { onlyOnce: true });
      }
    } catch (_) { }
  }

  // 3. Purger immédiatement le stockage de session local (mobile + web)
  try {
    const AsyncStorage = typeof require === 'function' ? (require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage')) : null;
    if (AsyncStorage) AsyncStorage.removeItem('driver2go_auth_session').catch(() => { });
  } catch (_) { }
  try {
    if (typeof localStorage !== 'undefined') {
      localStorage.removeItem('driver2go_auth_session');
    }
  } catch (_) { }

  // 4. Déclencher immédiatement la déconnexion dans Redux (sans délai artificiel)
  dispatch({
    type: USER_SIGN_OUT,
    payload: null
  });

  // 5. Déconnecter le module Firebase natif (mobile)
  const nativeAuthModule = getNativeAuthModule();
  if (nativeAuthModule && typeof nativeAuthModule === 'function') {
    try {
      const nativeAuth = nativeAuthModule();
      if (nativeAuth && typeof nativeAuth.signOut === 'function') {
        nativeAuth.signOut().catch(() => { });
      }
    } catch (_) { }
  }

  // 6. Déconnecter le SDK Web Firebase
  if (auth && typeof fbSignOut === 'function') {
    fbSignOut(auth).catch(() => { });
  }
};

export const signOut = signOff;

export const updateProfile = (updateData) => async (dispatch, getState) => {

  const {
    auth,
    config,
    singleUserRef,
    driverDocsRef,
    driverDocsRefBack,
    verifyIdImageRef
  } = firebase;

  const currentProfile = (typeof getState === 'function' ? getState()?.auth?.profile : store.getState()?.auth?.profile) || {};
  const uid = auth?.currentUser?.uid || currentProfile?.uid;
  if (!uid) return;

  const dataToUpdate = { ...updateData };

  // Sanitize dataToUpdate: non-admin users must not attempt to write protected fields directly to RTDB
  if (currentProfile?.usertype !== 'admin') {
    delete dataToUpdate.approved;
    delete dataToUpdate.walletBalance;
    delete dataToUpdate.usertype;
    delete dataToUpdate.accountDeleted;
    delete dataToUpdate.isDeleted;
    delete dataToUpdate.activeMode;
    delete dataToUpdate.modeSwitchLock;
    delete dataToUpdate.acceptLock;
    delete dataToUpdate.queue;
  }
  const bucket = firebase.config?.storageBucket || config?.storageBucket || (firebase.config?.projectId ? `${firebase.config.projectId}.firebasestorage.app` : "drive2go-c806b.firebasestorage.app");

  const uploadDocToStorage = async (refFn, objectSubPath, blobData) => {
    // 1. Try Firebase Storage JS SDK first
    if (refFn) {
      try {
        await uploadBytesResumable(refFn(uid), blobData);
        return await getDownloadURL(refFn(uid));
      } catch (sdkErr) {
        console.warn(`[updateProfile] SDK upload failed for ${objectSubPath}, switching to Storage REST:`, sdkErr.message);
      }
    }
    // 2. Fallback to Firebase Storage REST API with valid ID token
    const idToken = await getValidAuthToken();
    const objectPath = `users/${uid}/${objectSubPath}`;
    const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodeURIComponent(objectPath)}`;
    const headers = {
      'Content-Type': 'image/jpeg',
      ...(idToken ? { 'Authorization': `Firebase ${idToken}` } : {})
    };
    const res = await fetch(url, {
      method: 'POST',
      headers,
      body: blobData
    });
    if (!res.ok) {
      const errText = await res.text();
      throw new Error(`Storage upload failed (${res.status}): ${errText}`);
    }
    const json = await res.json();
    const token = json.downloadTokens ? json.downloadTokens.split(',')[0] : '';
    return `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media${token ? `&token=${token}` : ''}`;
  };

  // 1. Upload document image blobs to Firebase Storage, converting them to download URL strings
  if (dataToUpdate.licenseImage && typeof dataToUpdate.licenseImage !== 'string') {
    try {
      dataToUpdate.licenseImage = await uploadDocToStorage(driverDocsRef, 'license', dataToUpdate.licenseImage);
    } catch (e) {
      console.error("[updateProfile] licenseImage upload error:", e.message);
      throw new Error(`Erreur lors de l'envoi du permis (recto): ${e.message}`);
    }
  }
  if (dataToUpdate.licenseImageBack && typeof dataToUpdate.licenseImageBack !== 'string') {
    try {
      dataToUpdate.licenseImageBack = await uploadDocToStorage(driverDocsRefBack, 'licenseBack', dataToUpdate.licenseImageBack);
    } catch (e) {
      console.error("[updateProfile] licenseImageBack upload error:", e.message);
      throw new Error(`Erreur lors de l'envoi du permis (verso): ${e.message}`);
    }
  }
  if (dataToUpdate.verifyIdImage && typeof dataToUpdate.verifyIdImage !== 'string') {
    try {
      dataToUpdate.verifyIdImage = await uploadDocToStorage(verifyIdImageRef, 'verifyIdImage', dataToUpdate.verifyIdImage);
    } catch (e) {
      console.error("[updateProfile] verifyIdImage upload error:", e.message);
      throw new Error(`Erreur lors de l'envoi de la pièce d'identité: ${e.message}`);
    }
  }
  if (dataToUpdate.profile_image && typeof dataToUpdate.profile_image !== 'string') {
    try {
      dataToUpdate.profile_image = await uploadDocToStorage(profileImageRef, 'profileImage', dataToUpdate.profile_image);
    } catch (e) {
      console.error("[updateProfile] profile_image upload error:", e.message);
      throw new Error(`Erreur lors de l'envoi de la photo de profil: ${e.message}`);
    }
  }

  // 2. Now that image fields are guaranteed to be String URLs, update Redux store & AsyncStorage
  const updatedProfile = { ...currentProfile, ...dataToUpdate };

  const hasFirst = (updatedProfile.firstName || '').trim();
  const hasLast = (updatedProfile.lastName || '').trim();
  const hasMobile = (updatedProfile.mobile || '').trim();
  if (hasFirst.length > 0 && hasLast.length > 0 && hasFirst.toLowerCase() !== 'pas de nom' && hasMobile.length > 5) {
    updatedProfile.profileComplete = true;
    if (updatedProfile.usertype !== 'driver') {
      updatedProfile.approved = true;
    }
  }

  dispatch({
    type: FETCH_USER_SUCCESS,
    payload: updatedProfile
  });

  try {
    let AsyncStorage = null;
    try {
      AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
    } catch (_) { }
    if (AsyncStorage) {
      const raw = await AsyncStorage.getItem('driver2go_auth_session');
      if (raw) {
        const session = JSON.parse(raw);
        session.profile = updatedProfile;
        await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify(session));
      }
    }
  } catch (_) { }

  // 3. Update via Firebase JS SDK with clean data
  if (singleUserRef) {
    update(singleUserRef(uid), dataToUpdate).catch(err => {
      console.warn("[updateProfile] SDK update warning:", err.message);
    });
  }

  // 4. Always sync via RTDB REST to guarantee persistence
  try {
    let idToken = await getValidAuthToken();
    if (idToken) {
      const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
      const res = await fetch(`${rtdbBase}/users/${uid}.json?auth=${idToken}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(dataToUpdate)
      });
      if (res.status === 401 || res.status === 403) {
        idToken = await getValidAuthToken(true);
        if (idToken) {
          await fetch(`${rtdbBase}/users/${uid}.json?auth=${idToken}`, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(dataToUpdate)
          });
        }
      }
    }
  } catch (restErr) {
    console.warn("[updateProfile] REST sync warning:", restErr?.message);
  }

  // 5. If mobile was modified, trigger backend sync via updateAuthMobile to ensure Firebase Auth and RTDB Admin SDK update
  if (dataToUpdate.mobile) {
    try {
      updateAuthMobile(dataToUpdate.mobile).catch(() => {});
    } catch (_) {}
  }

  return updatedProfile;
};

export const getValidAuthToken = async (forceRefresh = false) => {
  const { config } = firebase;
  // 1. Check Native Mobile Firebase Auth (@react-native-firebase/auth)
  try {
    const nativeAuthFn = getNativeAuthModule();
    if (nativeAuthFn && typeof nativeAuthFn === 'function') {
      const nativeAuth = nativeAuthFn();
      if (nativeAuth?.currentUser && typeof nativeAuth.currentUser.getIdToken === 'function') {
        const token = await nativeAuth.currentUser.getIdToken(forceRefresh);
        if (token) return token;
      }
    }
  } catch (_) { }

  // 2. Check JS Web SDK Auth (firebase.auth)
  if (firebase.auth?.currentUser) {
    try {
      const token = await firebase.auth.currentUser.getIdToken(forceRefresh);
      if (token) return token;
    } catch (_) { }
  }

  let AsyncStorage = null;
  try {
    AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
  } catch (_) { }
  if (!AsyncStorage) return null;

  try {
    const raw = await AsyncStorage.getItem('driver2go_auth_session');
    if (!raw) return null;
    const session = JSON.parse(raw);
    if (!session) return null;

    const now = Date.now();
    const tokenAgeMs = now - (session.savedAt || 0);
    // Token is valid for 60 min; refresh if older than 45 min or forced
    if (session.idToken && tokenAgeMs < 45 * 60 * 1000 && !forceRefresh) {
      return session.idToken;
    }

    if (session.refreshToken) {
      const apiKey = firebase.config?.apiKey || config?.apiKey || "AIzaSyA5XP6fNPG-S_ZMlQbzi0o8yH8EBZtM7wQ";
      const authDomain = firebase.config?.authDomain || config?.authDomain || (firebase.config?.projectId ? `${firebase.config.projectId}.firebaseapp.com` : "drive2go-c806b.firebaseapp.com");
      const refreshRes = await fetch(`https://securetoken.googleapis.com/v1/token?key=${apiKey}`, {
        method: "POST",
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Referer": `https://${authDomain}`
        },
        body: `grant_type=refresh_token&refresh_token=${session.refreshToken}`
      });

      if (refreshRes.ok) {
        const refreshData = await refreshRes.json();
        if (refreshData?.id_token) {
          session.idToken = refreshData.id_token;
          if (refreshData.refresh_token) {
            session.refreshToken = refreshData.refresh_token;
          }
          session.savedAt = Date.now();
          await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify(session));
          return session.idToken;
        }
      } else {
        console.warn("[getValidAuthToken] Token refresh failed:", refreshRes.status);
      }
    }

    return session.idToken || null;
  } catch (err) {
    console.warn("[getValidAuthToken] Error:", err?.message);
    return null;
  }
};

export const refreshUserProfile = () => async (dispatch, getState) => {
  const { auth, config, singleUserRef } = firebase;
  const state = typeof getState === 'function' ? getState() : store.getState();
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid;
  if (!uid) return;
  const refreshEpoch = authSessionEpoch;

  // 1. Vérification préalable de la table des révocations publiques (revoked_users)
  try {
    const isRevoked = await checkAccountRevokedStatus(uid);
    if (isStaleAuthOperation(refreshEpoch, uid)) return;
    if (isRevoked) {
      console.warn("[refreshUserProfile] Account confirmed in revoked_users. Signing off immediately...");
      dispatch(signOff());
      return;
    }
  } catch (_) {}

  let p = null;
  if (singleUserRef && auth?.currentUser) {
    try {
      const snap = await get(singleUserRef(uid));
      if (isStaleAuthOperation(refreshEpoch, uid)) return;
      if (snap && snap.exists()) {
        p = snap.val();
      }
    } catch (e) {
      if (isStaleAuthOperation(refreshEpoch, uid)) return;
      if (e?.message && (e.message.includes("Permission denied") || e.message.includes("PERMISSION_DENIED"))) {
        console.warn("[refreshUserProfile] RTDB Permission denied on users/" + uid + ". Checking revocation...");
        const isRevoked = await checkAccountRevokedStatus(uid);
        if (isStaleAuthOperation(refreshEpoch, uid)) return;
        if (isRevoked) {
          dispatch(signOff());
          return;
        }
      }
    }
  }

  // Fallback REST via getValidAuthToken
  if (!p) {
    try {
      let token = await getValidAuthToken();
      if (isStaleAuthOperation(refreshEpoch, uid)) return;
      if (token) {
        const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        let rtdbUrl = `${rtdbBase}/users/${uid}.json?auth=${token}`;
        let res = await fetch(rtdbUrl);
        if (res.status === 401 || res.status === 403) {
          token = await getValidAuthToken(true);
          if (token) {
            res = await fetch(`${rtdbBase}/users/${uid}.json?auth=${token}`);
          }
        }
        if (isStaleAuthOperation(refreshEpoch, uid)) return;
        if (res.ok) {
          p = await res.json();
        } else if (res.status === 401 || res.status === 403) {
          const isRevoked = await checkAccountRevokedStatus(uid);
          if (isStaleAuthOperation(refreshEpoch, uid)) return;
          if (isRevoked) {
            dispatch(signOff());
            return;
          }
        }
      }
    } catch (_) { }
  }

  if (isStaleAuthOperation(refreshEpoch, uid)) return;

  if (p) {
    p.uid = uid;
    if (p.accountDeleted === true || p.isDeleted === true || p.isActive === false) {
      console.warn("[refreshUserProfile] Account marked deleted/inactive. Signing off immediately...");
      dispatch(signOff());
      return;
    }

    const latestState = typeof getState === 'function' ? getState() : store.getState();
    const prevProf = latestState?.auth?.profile || {};
    if (prevProf.uid && prevProf.uid !== uid) {
      return;
    }
    if (
      p.walletBalance !== prevProf.walletBalance ||
      !prevProf.firstName ||
      p.usertype !== prevProf.usertype ||
      p.driverActiveStatus !== prevProf.driverActiveStatus ||
      p.carType !== prevProf.carType ||
      p.approved !== prevProf.approved ||
      p.activeMode !== prevProf.activeMode ||
      p.driverApplicationStatus !== prevProf.driverApplicationStatus ||
      p.isActive !== prevProf.isActive ||
      p.queue !== prevProf.queue
    ) {
      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: p
      });
    }
    try {
      let AsyncStorage = null;
      try {
        AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
        if (AsyncStorage && !isStaleAuthOperation(refreshEpoch, uid)) {
          const raw = await AsyncStorage.getItem('driver2go_auth_session');
          if (raw && !isStaleAuthOperation(refreshEpoch, uid)) {
            const session = JSON.parse(raw);
            if (!session.uid || session.uid === uid) {
              session.profile = p;
              await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify(session));
            }
          }
        }
      } catch (_) { }
    } catch (_) { }
  }
};


export const updateProfileImage = (imageBlob) => async (dispatch, getState) => {

  const {
    auth,
    config,
    singleUserRef,
    profileImageRef,
  } = firebase;

  const currentProfile = (typeof getState === 'function' ? getState()?.auth?.profile : store.getState()?.auth?.profile) || {};
  const uid = auth?.currentUser?.uid || currentProfile?.uid;
  if (!uid) return { success: false, error: "No user UID" };

  try {
    const bucket = firebase.config?.storageBucket || config?.storageBucket || (firebase.config?.projectId ? `${firebase.config.projectId}.firebasestorage.app` : "drive2go-c806b.firebasestorage.app");
    let downloadUrl = null;

    // 1. Try Firebase Storage JS SDK first
    if (profileImageRef) {
      try {
        await uploadBytesResumable(profileImageRef(uid), imageBlob);
        downloadUrl = await getDownloadURL(profileImageRef(uid));
      } catch (sdkErr) {
        console.warn("[updateProfileImage] SDK upload failed, switching to Storage REST:", sdkErr.message);
      }
    }

    // 2. Fallback to Firebase Storage REST API with valid ID token
    if (!downloadUrl) {
      const idToken = await getValidAuthToken();
      const objectPath = `users/${uid}/profileImage`;
      const url = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodeURIComponent(objectPath)}`;
      const headers = {
        'Content-Type': 'image/jpeg',
        ...(idToken ? { 'Authorization': `Firebase ${idToken}` } : {})
      };
      const res = await fetch(url, {
        method: 'POST',
        headers,
        body: imageBlob
      });
      if (!res.ok) {
        const errText = await res.text();
        throw new Error(`Storage upload failed (${res.status}): ${errText}`);
      }
      const json = await res.json();
      const token = json.downloadTokens ? json.downloadTokens.split(',')[0] : '';
      downloadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o/${encodeURIComponent(objectPath)}?alt=media${token ? `&token=${token}` : ''}`;
    }

    if (downloadUrl) {
      if (typeof imageBlob?.close === 'function') {
        try { imageBlob.close(); } catch (_) { }
      }

      // 3. Update RTDB via SDK
      if (singleUserRef) {
        update(singleUserRef(uid), { profile_image: downloadUrl }).catch(() => { });
      }

      // 4. Update RTDB via REST fallback
      try {
        const idToken = await getValidAuthToken();
        if (idToken) {
          const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
          const rtdbUrl = `${rtdbBase}/users/${uid}.json?auth=${idToken}`;
          fetch(rtdbUrl, {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ profile_image: downloadUrl })
          }).catch(() => { });
        }
      } catch (_) { }

      // 5. Update Redux store immediately
      const updatedProfile = { ...currentProfile, profile_image: downloadUrl };
      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: updatedProfile
      });

      // 6. Update AsyncStorage session
      try {
        let AsyncStorage = null;
        try {
          AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
        } catch (_) { }
        if (AsyncStorage) {
          const raw = await AsyncStorage.getItem('driver2go_auth_session');
          if (raw) {
            const session = JSON.parse(raw);
            session.profile = updatedProfile;
            await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify(session));
          }
        }
      } catch (_) { }

      return { success: true, url: downloadUrl };
    }
    return { success: false, error: "Download URL missing" };
  } catch (err) {
    console.error("[updateProfileImage] Error:", err.message);
    return { success: false, error: err.message };
  }
};

export const updateWebProfileImage = (imageBlob) => async (dispatch, getState) => {

  const {
    auth,
    singleUserRef,
    profileImageRef
  } = firebase;

  const currentProfile = (typeof getState === 'function' ? getState()?.auth?.profile : store.getState()?.auth?.profile) || {};
  const uid = auth?.currentUser?.uid || currentProfile?.uid;
  if (!uid || !profileImageRef || !singleUserRef) return;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  await update(singleUserRef(uid), { profile_image: image });

  const updatedProfile = { ...currentProfile, profile_image: image };
  dispatch({
    type: FETCH_USER_SUCCESS,
    payload: updatedProfile
  });
};
export const updateCustomerProfileImage = async (imageBlob, id) => {

  const {
    singleUserRef,
    profileImageRef
  } = firebase;

  const uid = id;
  if (!uid || !profileImageRef || !singleUserRef) return;

  await uploadBytesResumable(profileImageRef(uid), imageBlob);
  let image = await getDownloadURL(profileImageRef(uid));
  update(singleUserRef(uid), { profile_image: image });

};

export const updatePushToken = (token, platform) => async (dispatch) => {

  const {
    auth,
    database
  } = firebase;

  const uid = auth?.currentUser?.uid || store.getState()?.auth?.profile?.uid;
  if (!uid || !token) return;

  // 1. Direct child set via RTDB WebSocket (allowed by rules for pushToken and userPlatform)
  if (database) {
    try {
      const { ref: dbRef, set: dbSet } = require("firebase/database");
      if (token) await dbSet(dbRef(database, `users/${uid}/pushToken`), token);
      if (platform) await dbSet(dbRef(database, `users/${uid}/userPlatform`), platform);
    } catch (err) {
      console.warn('[updatePushToken] RTDB direct child set failed:', err.message);
    }
  }

  // 2. Direct child set via REST PUT with auth token
  let idToken = null;
  try {
    idToken = await getValidAuthToken();
    if (idToken) {
      const config = firebase.config;
      const rtdbBase = (config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
      if (token) {
        await fetch(`${rtdbBase}/users/${uid}/pushToken.json?auth=${idToken}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(token)
        });
      }
      if (platform) {
        await fetch(`${rtdbBase}/users/${uid}/userPlatform.json?auth=${idToken}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(platform)
        });
      }
    }
  } catch (restErr) {
    console.warn('[updatePushToken] REST fallback failed:', restErr.message);
  }

  // 3. Reliable Admin SDK update via Cloud Function
  try {
    if (!idToken) {
      idToken = await getValidAuthToken();
    }
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const isLocalhost = origin && (origin.includes("localhost") || origin.includes("127.0.0.1"));
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : (!isLocalhost && origin ? origin : "https://europe-west1-drive2go-c806b.cloudfunctions.net");
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    await fetch(`${functionsUrl}/update_push_token`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ token, platform })
    });
  } catch (cfErr) {
    console.warn('[updatePushToken] Cloud function sync notice:', cfErr?.message);
  }

  try {
    const currentProfile = store.getState()?.auth?.profile;
    if (currentProfile) {
      dispatch({
        type: FETCH_USER_SUCCESS,
        payload: {
          ...currentProfile,
          pushToken: token,
          userPlatform: platform
        }
      });
    }
  } catch (_) { }
};

export const clearLoginError = () => (dispatch) => {
  dispatch({
    type: CLEAR_LOGIN_ERROR,
    payload: null
  });
};

export const fetchWalletHistory = () => (dispatch, getState) => {
  const {
    auth,
    config,
    walletHistoryRef
  } = firebase;

  const state = typeof getState === 'function' ? getState() : null;
  const uid = auth?.currentUser?.uid || state?.auth?.profile?.uid || store.getState()?.auth?.profile?.uid;

  if (!uid) {
    return;
  }

  const loadViaRest = async () => {
    let loaded = false;
    try {
      let token = await getValidAuthToken();
      if (token) {
        const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        let rtdbUrl = `${rtdbBase}/walletHistory/${uid}.json?auth=${token}`;
        let res = await fetch(rtdbUrl);
        if (res.status === 401) {
          token = await getValidAuthToken(true);
          if (token) {
            res = await fetch(`${rtdbBase}/walletHistory/${uid}.json?auth=${token}`);
          }
        }
        if (res.ok) {
          const data = await res.json();
          if (data && typeof data === 'object') {
            const arr = Object.keys(data).map(i => {
              data[i].id = i;
              return data[i];
            });
            dispatch({
              type: UPDATE_USER_WALLET_HISTORY,
              payload: arr.reverse()
            });
            loaded = true;
          } else {
            dispatch({
              type: UPDATE_USER_WALLET_HISTORY,
              payload: []
            });
            loaded = true;
          }
        }
      }
    } catch (_) { }

    if (!loaded) {
      const current = store.getState()?.auth?.walletHistory;
      if (!Array.isArray(current) || current.length > 0) {
        dispatch({
          type: UPDATE_USER_WALLET_HISTORY,
          payload: []
        });
      }
    }
  };

  if (!auth?.currentUser && walletHistoryRef) {
    loadViaRest();
    return;
  }

  if (walletHistoryRef) {
    try {
      off(walletHistoryRef(uid));
    } catch (_) { }

    onValue(walletHistoryRef(uid), snapshot => {
      const data = snapshot.val();
      if (data) {
        const arr = Object.keys(data).map(i => {
          data[i].id = i;
          return data[i];
        });
        dispatch({
          type: UPDATE_USER_WALLET_HISTORY,
          payload: arr.reverse()
        });
      } else {
        dispatch({
          type: UPDATE_USER_WALLET_HISTORY,
          payload: []
        });
      }
    }, async error => {
      console.warn("[fetchWalletHistory] DB read error:", error?.message);
      await loadViaRest();
    });
  } else {
    loadViaRest();
  }
};
export const fetchUserWalletHistory = (userId) => (dispatch) => {
  const {
    auth,
    walletHistoryRef
  } = firebase;

  const uid = userId;

  onValue(walletHistoryRef(uid), snapshot => {
    const data = snapshot.val();
    if (data) {
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: arr.reverse()
      });
    } else {
      dispatch({
        type: UPDATE_USER_WALLET_HISTORY,
        payload: []
      });
    }
  });
};

export const sendResetMail = (email) => async (dispatch) => {
  const { auth, config } = firebase;
  const normalizedEmail = email ? email.trim().toLowerCase() : "";

  logAuth("[RESET_PW] click", { email: normalizedEmail });

  dispatch({
    type: SEND_RESET_EMAIL,
    payload: normalizedEmail
  });

  // Resolve target host
  const origin = (typeof window !== "undefined" && window.location) ? window.location.origin : null;
  const functionsUrl = (typeof process !== "undefined" && process.env && process.env.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, "")
    : null;
  const activeProjectId = config?.projectId || firebase.config?.projectId || "drive2go-c806b";
  const host = origin || functionsUrl || `https://${activeProjectId}.web.app`;
  const endpoint = `${host}/request_password_reset`;

  // Detect locale
  let locale = "fr";
  if (typeof window !== "undefined" && window.location?.pathname?.startsWith("/en")) {
    locale = "en";
  }

  let backendUnavailable = false;
  let backendError = null;

  try {
    logAuth("[RESET_PW] backend_call_start", { endpoint });

    // 10-second timeout for backend response
    const controller = typeof AbortController !== "undefined" ? new AbortController() : null;
    const timeoutId = controller ? setTimeout(() => controller.abort(), 10000) : null;

    let response;
    try {
      response = await fetch(endpoint, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail, locale }),
        signal: controller ? controller.signal : undefined
      });
    } finally {
      if (timeoutId) clearTimeout(timeoutId);
    }

    if (response.ok) {
      logAuth("[RESET_PW] backend_call_success");
      return { success: true };
    }

    const status = response.status;
    const data = await response.json().catch(() => ({}));

    // 4xx errors: Validation (400) or Rate Limiting (429) -> NEVER FALLBACK!
    if (status >= 400 && status < 500) {
      const err = new Error(data.message || data.error || "Requête invalide");
      err.code = status === 429 ? "auth/too-many-requests" : (data.error || "auth/invalid-email");
      logAuth("[RESET_PW] backend_4xx_rejected_no_fallback", { status, code: err.code });
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: { code: err.code, message: err.message }
      });
      throw err;
    }

    // 5xx errors: Technical failure -> candidate for fallback
    backendUnavailable = true;
    backendError = new Error(`Backend 5xx error: ${status}`);
    backendError.code = "BACKEND_5XX";
    console.warn(`[RESET_PW] Backend returned 5xx (${status}), activating fallback:`, data);

  } catch (networkOrAbortError) {
    // If it was an explicit 4xx error thrown above, rethrow immediately without fallback
    if (
      networkOrAbortError.code === "auth/too-many-requests" ||
      networkOrAbortError.code === "auth/invalid-email" ||
      networkOrAbortError.code === "INVALID_EMAIL"
    ) {
      throw networkOrAbortError;
    }
    // Network failure / timeout / DNS drop -> technical unavailability
    backendUnavailable = true;
    backendError = networkOrAbortError;
    console.warn("[RESET_PW] Backend network/unavailability error, activating fallback:", networkOrAbortError);
  }

  // Fallback ONLY if backend is genuinely unavailable (network drop, timeout, 5xx)
  if (backendUnavailable) {
    console.warn("[RESET_PW] Activating Firebase fallback mechanism...");
    logAuth("[RESET_PW] activating_firebase_fallback", { reason: backendError?.message });
    try {
      const actionCodeSettings = {
        url: `${origin || (config?.authDomain ? `https://${config.authDomain}` : (firebase.config?.authDomain ? `https://${firebase.config.authDomain}` : "https://booking.driver2go.fr"))}/login`,
        handleCodeInApp: true,
      };
      try {
        await sendPasswordResetEmail(auth, normalizedEmail, actionCodeSettings);
      } catch (sendErr) {
        console.warn("[RESET_PW] Fallback with actionCodeSettings failed, retrying plain standard:", sendErr);
        await sendPasswordResetEmail(auth, normalizedEmail);
      }
      logAuth("[RESET_PW] firebase_fallback_success");
      return { success: true, fallback: true };
    } catch (fbError) {
      logAuth("[RESET_PW] firebase_fallback_error", { code: fbError.code, message: fbError.message });
      dispatch({
        type: SEND_RESET_EMAIL_FAILED,
        payload: { code: fbError.code, message: fbError.message }
      });
      throw fbError;
    }
  }
};

export const verifyUserPasswordResetCode = (oobCode) => async () => {
  const { auth } = firebase;
  logAuth("[RESET_PW] verify_code_start");
  const email = await verifyPasswordResetCode(auth, oobCode);
  logAuth("[RESET_PW] verify_code_success", { email });
  return email;
};

export const confirmUserPasswordReset = (oobCode, newPassword) => async () => {
  const { auth } = firebase;
  logAuth("[RESET_PW] confirm_reset_start");
  await confirmPasswordReset(auth, oobCode, newPassword);
  logAuth("[RESET_PW] confirm_reset_success");
  return { success: true };
};

export const verifyEmailPassword = (email, pass) => async (dispatch) => {
  const {
    auth,
    authRef,
    singleUserRef,
    config
  } = firebase;

  dispatch({ type: USER_SIGN_IN, payload: null });

  const cleanEmail = (email || '').trim();
  const apiKey = (config && config.apiKey) || (firebase.config && firebase.config.apiKey) || "AIzaSyA5XP6fNPG-S_ZMlQbzi0o8yH8EBZtM7wQ";
  const authDomain = (config && config.authDomain) || (firebase.config && firebase.config.authDomain) || "drive2go-c806b.firebaseapp.com";
  const refererUrl = `https://${authDomain}`;

  try {
    // 1. Authenticate via Google Identity Toolkit REST API with required Referer header
    const restUrl = `https://identitytoolkit.googleapis.com/v1/accounts:signInWithPassword?key=${apiKey}`;
    const response = await fetch(restUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Referer': refererUrl,
        'X-Firebase-Locale': 'fr'
      },
      body: JSON.stringify({
        email: cleanEmail,
        password: pass,
        returnSecureToken: true
      })
    });

    const resData = await response.json();

    if (!response.ok || resData.error) {
      const errCode = resData?.error?.message;
      let friendlyMsg = "Email ou mot de passe incorrect.";
      if (errCode === 'EMAIL_NOT_FOUND' || errCode === 'INVALID_PASSWORD' || errCode === 'INVALID_LOGIN_CREDENTIALS') {
        friendlyMsg = "Email ou mot de passe incorrect.";
      } else if (errCode === 'USER_DISABLED') {
        friendlyMsg = "Ce compte utilisateur a été désactivé.";
      } else if (errCode === 'TOO_MANY_ATTEMPTS_TRY_LATER') {
        friendlyMsg = "Trop de tentatives infructueuses. Veuillez réessayer plus tard.";
      } else if (resData?.error?.message) {
        friendlyMsg = resData.error.message;
      }
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: {
          code: errCode || 'auth/login-failed',
          message: friendlyMsg
        }
      });
      return;
    }

    // 2. Authentication succeeded! Extract user data
    const uid = resData.localId;
    const idToken = resData.idToken;
    if (currentActiveAuthUid && currentActiveAuthUid !== uid) {
      detachUserRealtimeListeners(currentActiveAuthUid);
    }
    const loginEpoch = ++authSessionEpoch;
    currentActiveAuthUid = uid;
    let profile = null;

    // Fetch profile from database
    if (uid && singleUserRef) {
      try {
        const snap = await get(singleUserRef(uid));
        if (snap && snap.exists()) {
          profile = snap.val();
        }
      } catch (dbErr) {
        console.warn("[verifyEmailPassword] singleUserRef get warning:", dbErr?.message);
      }
    }

    // Fallback profile via RTDB REST if singleUserRef is not initialized
    if (!profile && uid) {
      try {
        const rtdbBase = (config?.databaseURL || firebase.config?.databaseURL || 'https://drive2go-c806b-default-rtdb.europe-west1.firebasedatabase.app').replace(/\/$/, '');
        const rtdbUrl = `${rtdbBase}/users/${uid}.json?auth=${idToken}`;
        const rtdbRes = await fetch(rtdbUrl);
        if (rtdbRes.ok) {
          profile = await rtdbRes.json();
        }
      } catch (rtdbErr) {
        console.warn("[verifyEmailPassword] RTDB REST fallback warning:", rtdbErr?.message);
      }
    }

    // Fallback check_auth_exists if profile or usertype is still missing
    if ((!profile || !profile.usertype) && uid) {
      try {
        const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
        const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL) ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
        const activeProjectId = config?.projectId || firebase.config?.projectId || "drive2go-c806b";
        const region = (typeof process !== "undefined" && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || firebase.config?.functionsRegion || "europe-west1";
        let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : `https://${region}-${activeProjectId}.cloudfunctions.net`);
        const checkRes = await fetch(`${host}/check_auth_exists`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(idToken ? { 'Authorization': `Bearer ${idToken}` } : {})
          },
          body: JSON.stringify({
            uid,
            email: resData.email || cleanEmail,
            firstName: resData.displayName || '',
            source: typeof document === 'undefined' ? 'mobile_app' : 'web_app'
          })
        });
        if (checkRes.ok) {
          const checkJson = await checkRes.json();
          if (checkJson && checkJson.uid) {
            profile = { ...checkJson, ...(profile || {}), uid };
          }
        }
      } catch (_) {}
    }

    if (isStaleAuthOperation(loginEpoch, uid)) {
      return;
    }

    if (!profile) {
      profile = {
        uid,
        email: resData.email || cleanEmail,
        mobile: '',
        firstName: resData.displayName || '',
        lastName: '',
        usertype: 'customer'
      };
    }
    if (!profile.usertype) {
      profile.usertype = 'customer';
    }
    profile.uid = uid;

    // Vérifier si le compte est marqué supprimé ou inactif
    if (profile.accountDeleted === true || profile.isDeleted === true || profile.isActive === false) {
      console.warn("[verifyEmailPassword] User account is deleted/inactive. Refusing login.");
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: {
          code: 'auth/user-disabled',
          message: 'Ce compte utilisateur a été supprimé ou désactivé.'
        }
      });
      return;
    }

    // Persist session locally
    try {
      let AsyncStorage = null;
      try {
        AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
      } catch (_) { }
      if (AsyncStorage && !isStaleAuthOperation(loginEpoch, uid)) {
        await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify({
          uid,
          email: resData.email || cleanEmail,
          idToken,
          refreshToken: resData.refreshToken,
          profile,
          savedAt: Date.now()
        }));
      }
    } catch (_) { }

    if (isStaleAuthOperation(loginEpoch, uid)) {
      return;
    }

    // Dispatch login success into Redux store
    dispatch({
      type: FETCH_USER_SUCCESS,
      payload: profile
    });

    // Attacher l'observateur temps réel pour forcer la déconnexion instantanée si supprimé par l'admin
    attachUserSessionWatcher(uid, dispatch);

    // In background, sync SDK auth session if available
    let currentAuth = auth || (typeof authRef === 'function' ? authRef() : null);
    if (currentAuth) {
      try {
        signInWithEmailAndPassword(currentAuth, cleanEmail, pass).catch(() => { });
      } catch (_) { }
    }

  } catch (netErr) {
    console.error("[verifyEmailPassword] Network/auth error:", netErr);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: {
        code: 'auth/network-request-failed',
        message: 'Erreur réseau. Veuillez vérifier votre connexion internet.'
      }
    });
  }
};

export const requestMobileOtp = (mobileData) => async (dispatch) => {
  const {
    config
  } = firebase;
  dispatch({
    type: REQUEST_OTP,
    payload: true
  });

  // [FIX] Host Logic
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
  let host = origin || functionsUrl || `https://${config.projectId}.web.app`;
  let url = `${host}/request_mobile_otp`;
  try {
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(typeof mobileData === 'object' ? mobileData : { mobile: mobileData })
    });
    const result = await response.json();
    if (result.success) {
      dispatch({
        type: REQUEST_OTP_SUCCESS,
        payload: true
      });
    } else {
      dispatch({
        type: REQUEST_OTP_FAILED,
        payload: result.error
      });
    }
    return result;
  } catch (error) {
    console.log(error);
    return { error: error.message };
  }
}

export const verifyMobileOtp = (mobile, otp) => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  const body = {
    mobile: mobile,
    otp: otp
  };
  try {
    // [FIX] Host Logic
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = process.env.REACT_APP_FUNCTIONS_URL ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '') : null;
    let host = origin || functionsUrl || `https://${config.projectId}.web.app`;
    let url = `${host}/verify_mobile_otp`;
    const response = await fetch(url, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(body)
    })
    const result = await response.json();
    if (result.token) {
      signInWithCustomToken(auth, result.token)
        .then((user) => {
          //OnAuthStateChange takes care of Navigation
        })
        .catch((error) => {
          dispatch({
            type: USER_SIGN_IN_FAILED,
            payload: error
          });
        });
    } else {
      dispatch({
        type: USER_SIGN_IN_FAILED,
        payload: result.error
      });
    }
  } catch (error) {
    console.log(error);
    dispatch({
      type: USER_SIGN_IN_FAILED,
      payload: error
    });
  }
}

export const updateAuthMobile = async (mobile, otp) => {
  const {
    auth,
    config
  } = firebase;

  let uid = auth?.currentUser?.uid || store.getState()?.auth?.profile?.uid;
  if (!uid) {
    try {
      const nativeAuthFn = getNativeAuthModule();
      if (nativeAuthFn && typeof nativeAuthFn === 'function') {
        uid = nativeAuthFn()?.currentUser?.uid;
      }
    } catch (_) { }
  }
  const body = {
    uid: uid,
    mobile: mobile,
    otp: otp
  };

  // [FIX] Host Logic
  const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
  const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : null;
  let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
  let url = `${host}/update_auth_mobile`;
  try {
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify(body)
    });
    const result = await response.json();
    if (result && result.success) {
      return { success: true };
    } else {
      return { success: false, error: result?.error || result?.message || 'Error updating user' };
    }
  } catch (error) {
    return { success: false, error: error };
  }
};

export const requestEmailChangeOtp = (newEmail, locale = 'fr') => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/request_email_change_otp`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ newEmail, locale })
    });
    const result = await response.json();
    return result;
  } catch (error) {
    return { error: 'NETWORK_ERROR', message: error.message };
  }
};

export const verifyEmailChangeOtp = (otp, locale = 'fr') => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/verify_email_change_otp`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ otp, locale })
    });
    const result = await response.json();
    if (result.success && auth?.currentUser) {
      await auth.currentUser.reload().catch(() => { });
    }
    return result;
  } catch (error) {
    return { error: 'NETWORK_ERROR', message: error.message };
  }
};

export const sendPasswordChangeAlert = (locale = 'fr') => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/send_password_change_alert`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ locale })
    });
    const result = await response.json();
    return result;
  } catch (error) {
    return { error: 'NETWORK_ERROR', message: error.message };
  }
};

export const requestSetPasswordOtp = (locale = 'fr') => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/request_set_password_otp`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ locale })
    });
    const result = await response.json();
    return result;
  } catch (error) {
    return { error: 'NETWORK_ERROR', message: error.message };
  }
};

export const verifySetPasswordOtp = (otp, newPassword, locale = 'fr') => async (dispatch) => {
  const {
    auth,
    config
  } = firebase;
  try {
    const origin = (typeof window !== 'undefined' && window.location) ? window.location.origin : null;
    const functionsUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
      ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
      : null;
    let host = origin || functionsUrl || (config?.projectId ? `https://${config.projectId}.web.app` : 'https://drive2go-c806b.web.app');
    let url = `${host}/verify_set_password_otp`;
    const idToken = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (idToken) headers['Authorization'] = `Bearer ${idToken}`;
    const response = await fetch(url, {
      method: 'POST',
      headers,
      body: JSON.stringify({ otp, newPassword, locale })
    });
    const result = await response.json();
    if (result.success && auth?.currentUser) {
      await auth.currentUser.reload().catch(() => { });
    }
    return result;
  } catch (error) {
    return { error: 'NETWORK_ERROR', message: error.message };
  }
};

/**
 * Retourne true uniquement si le profil correspond à un chauffeur officiellement approuvé et actif.
 * Un compte chauffeur non approuvé, suspendu ou supprimé retourne toujours false.
 */
export const isApprovedActiveDriver = (profile) => {
  return Boolean(
    profile &&
    profile.usertype === 'driver' &&
    profile.approved === true &&
    profile.accountDeleted !== true &&
    profile.isDeleted !== true &&
    profile.isActive !== false
  );
};

/**
 * Calcule le rôle effectif d'utilisation de l'interface (sans jamais remplacer les contrôles serveur).
 * - 'admin' ou 'fleetadmin' conservent leur rôle.
 * - Un chauffeur approuvé et actif peut être en 'customer' (mode passager) ou 'driver' (par défaut).
 * - Tout autre compte (client, chauffeur non approuvé, suspendu ou supprimé) est traité comme 'customer'.
 */
export const getEffectiveUserRole = (profile) => {
  if (!profile) return 'customer';
  if (profile.usertype === 'admin' || profile.usertype === 'fleetadmin') {
    return profile.usertype;
  }
  if (isApprovedActiveDriver(profile)) {
    return profile.activeMode === 'customer' ? 'customer' : 'driver';
  }
  return 'customer';
};

export const switchDriverActiveMode = (targetMode) => async (dispatch, getState) => {
  const { auth, config } = firebase;
  const state = typeof getState === 'function' ? getState() : store.getState();
  const currentProfile = state?.auth?.profile || store.getState()?.auth?.profile || {};
  const uid = auth?.currentUser?.uid || currentProfile?.uid;

  if (!uid) {
    return { success: false, error: 'UNAUTHENTICATED', message: 'Utilisateur non connecté.' };
  }
  if (targetMode !== 'driver' && targetMode !== 'customer') {
    return { success: false, error: 'INVALID_TARGET_MODE', message: 'Mode invalide.' };
  }

  const envUrl = (typeof process !== 'undefined' && process.env?.REACT_APP_FUNCTIONS_URL)
    ? process.env.REACT_APP_FUNCTIONS_URL.replace(/\/+$/, '')
    : '';
  const projectId = config?.projectId || firebase.config?.projectId || 'drive2go-c806b';
  const region = (typeof process !== 'undefined' && (process.env?.REACT_APP_FUNCTIONS_REGION || process.env?.FUNCTIONS_REGION)) || config?.functionsRegion || firebase.config?.functionsRegion || 'europe-west1';
  const baseUrl = envUrl || `https://${region}-${projectId}.cloudfunctions.net`;

  try {
    let token = await getValidAuthToken();
    const headers = { 'Content-Type': 'application/json' };
    if (token) {
      headers['Authorization'] = `Bearer ${token}`;
    }

    let response = await fetch(`${baseUrl}/switchDriverActiveMode`, {
      method: 'POST',
      headers,
      body: JSON.stringify({ targetMode })
    });

    if (response.status === 401) {
      token = await getValidAuthToken(true);
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        response = await fetch(`${baseUrl}/switchDriverActiveMode`, {
          method: 'POST',
          headers,
          body: JSON.stringify({ targetMode })
        });
      }
    }

    const result = await response.json().catch(() => ({}));
    if (!response.ok || !result?.success) {
      return {
        success: false,
        status: response.status,
        error: result?.error || 'MODE_SWITCH_FAILED',
        message: result?.message || 'Impossible de changer de mode.'
      };
    }

    const updatedProfile = {
      ...currentProfile,
      ...(result.profile && typeof result.profile === 'object' ? result.profile : {}),
      uid,
      usertype: 'driver',
      approved: true,
      activeMode: targetMode,
      driverActiveStatus: false
    };

    dispatch({
      type: FETCH_USER_SUCCESS,
      payload: updatedProfile
    });

    try {
      let AsyncStorage = null;
      try {
        AsyncStorage = require('@react-native-async-storage/async-storage').default || require('@react-native-async-storage/async-storage');
      } catch (_) { }
      if (AsyncStorage) {
        const raw = await AsyncStorage.getItem('driver2go_auth_session');
        if (raw) {
          const session = JSON.parse(raw);
          session.profile = updatedProfile;
          await AsyncStorage.setItem('driver2go_auth_session', JSON.stringify(session));
        }
      }
    } catch (_) { }

    return {
      success: true,
      activeMode: targetMode,
      driverActiveStatus: false,
      revokedOffersCount: result.revokedOffersCount || 0,
      profile: updatedProfile
    };
  } catch (error) {
    return {
      success: false,
      error: 'NETWORK_ERROR',
      message: error?.message || 'Erreur réseau lors du changement de mode.'
    };
  }
};
